import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, User, Bell, Heart, LogOut, Shield, LayoutDashboard, BellRing } from "lucide-react";
import { pushSupported, pushStatus, subscribePush, unsubscribePush } from "@/lib/push";

const NOTIF_LABELS = { live: "Un média suivi démarre un direct", new_media: "Nouveau média dans ma province", replies: "Réponse à mes commentaires", support: "Confirmation de soutien", admin: "Annonces administratives" };

export default function Profile() {
  const { user, ready, updateUser, logout } = useAuth();
  const [provinces, setProvinces] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [f, setF] = useState(null);
  const [supports, setSupports] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => { if (pushSupported()) pushStatus().then(setPushOn); }, []);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushOn) { await unsubscribePush(); setPushOn(false); toast.success("Notifications push désactivées"); }
      else { await subscribePush(); setPushOn(true); toast.success("Notifications push activées !"); }
    } catch (e) { toast.error(e.message || "Action impossible"); }
    finally { setPushBusy(false); }
  };

  useEffect(() => {
    if (!user) return;
    setF({ first_name: user.first_name, last_name: user.last_name, phone: user.phone || "", province: user.province || "", city: user.city || "", preferred_language: user.preferred_language || "Francais", notification_prefs: user.notification_prefs || {} });
    api.get("/provinces").then((r) => setProvinces(r.data));
    api.get("/languages").then((r) => setLanguages(r.data));
    api.get("/me/supports").then((r) => setSupports(r.data));
    api.get("/me/notifications").then((r) => setNotifs(r.data));
  }, [user]);

  if (ready && !user) return (
    <div className="max-w-lg mx-auto px-4 text-center py-24">
      <User className="mx-auto text-primary mb-3" size={40} />
      <h1 className="font-heading font-bold text-2xl">Mon profil</h1>
      <p className="text-muted-foreground mt-2">Connectez-vous pour accéder à votre profil.</p>
      <Link to="/login" className="inline-flex mt-5 rounded-full bg-primary text-white px-6 py-2.5 font-semibold">Connexion</Link>
    </div>
  );
  if (!f) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-primary" size={32} /></div>;

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setNotif = (k, v) => setF((p) => ({ ...p, notification_prefs: { ...p.notification_prefs, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/auth/profile", f); updateUser(data); toast.success("Profil mis à jour"); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 mt-6">
      <div className="flex items-center gap-4 mb-6">
        {user.avatar ? <img src={user.avatar} alt="" className="w-16 h-16 rounded-2xl" /> :
          <span className="w-16 h-16 rounded-2xl bg-primary text-white flex items-center justify-center text-2xl font-heading font-bold">{user.first_name?.[0]}</span>}
        <div>
          <h1 className="font-heading font-extrabold text-2xl tracking-tight">{user.name}</h1>
          <p className="text-sm text-muted-foreground">{user.email}</p>
          <span className="inline-block mt-1 rounded-md bg-secondary px-2 py-0.5 text-[11px] font-medium capitalize">{user.role === "admin" ? "Super Admin" : user.role === "manager" ? "Gestionnaire" : "Utilisateur"}</span>
        </div>
      </div>

      {(user.role === "admin" || user.role === "manager") && (
        <div className="flex gap-2 mb-6">
          {user.role === "admin" && <Link to="/admin" className="flex items-center gap-2 rounded-xl bg-rdcred/10 text-rdcred px-4 py-2.5 text-sm font-semibold hover:bg-rdcred/20"><Shield size={16} /> Espace Super Admin</Link>}
          <Link to="/manager" className="flex items-center gap-2 rounded-xl bg-rdcgold/10 text-rdcgold px-4 py-2.5 text-sm font-semibold hover:bg-rdcgold/20"><LayoutDashboard size={16} /> Espace Média</Link>
        </div>
      )}

      <Tabs defaultValue="profile">
        <TabsList className="mb-5">
          <TabsTrigger value="profile" data-testid="tab-profile"><User size={15} className="mr-1.5" /> Profil</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications"><Bell size={15} className="mr-1.5" /> Notifications</TabsTrigger>
          <TabsTrigger value="supports" data-testid="tab-supports"><Heart size={15} className="mr-1.5" /> Mes soutiens</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <div className="rounded-2xl border border-border bg-card p-5 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Input value={f.first_name} onChange={(e) => set("first_name", e.target.value)} placeholder="Prénom" data-testid="profile-firstname" />
              <Input value={f.last_name} onChange={(e) => set("last_name", e.target.value)} placeholder="Nom" data-testid="profile-lastname" />
            </div>
            <Input value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Téléphone" data-testid="profile-phone" />
            <div className="grid grid-cols-2 gap-3">
              <Select value={f.province} onValueChange={(v) => set("province", v)}>
                <SelectTrigger data-testid="profile-province"><SelectValue placeholder="Ma province" /></SelectTrigger>
                <SelectContent>{provinces.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
              <Input value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="Ville" data-testid="profile-city" />
            </div>
            <Select value={f.preferred_language} onValueChange={(v) => set("preferred_language", v)}>
              <SelectTrigger data-testid="profile-language"><SelectValue placeholder="Langue préférée" /></SelectTrigger>
              <SelectContent>{languages.map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={save} disabled={saving} className="w-full font-semibold" data-testid="profile-save">
              {saving ? <Loader2 className="animate-spin" size={18} /> : "Enregistrer"}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="notifications">
          <div className="rounded-2xl border border-border bg-card p-5 mb-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0"><BellRing size={18} /></span>
                <div>
                  <p className="font-semibold text-sm">Notifications push</p>
                  <p className="text-xs text-muted-foreground">Recevez les alertes directement sur votre appareil, même quand l'application est fermée.</p>
                </div>
              </div>
              <Button onClick={togglePush} disabled={pushBusy || !pushSupported()} variant={pushOn ? "outline" : "default"} className="shrink-0 font-semibold" data-testid="push-toggle">
                {pushBusy ? <Loader2 className="animate-spin" size={16} /> : pushOn ? "Désactiver" : "Activer"}
              </Button>
            </div>
            {!pushSupported() && <p className="text-[11px] text-muted-foreground mt-2">Non supporté sur cet appareil / navigateur.</p>}
          </div>
          <div className="rounded-2xl border border-border bg-card p-5 space-y-4">
            {Object.entries(NOTIF_LABELS).map(([k, label]) => (
              <div key={k} className="flex items-center justify-between">
                <span className="text-sm">{label}</span>
                <Switch checked={!!f.notification_prefs[k]} onCheckedChange={(v) => setNotif(k, v)} data-testid={`notif-${k}`} />
              </div>
            ))}
            <Button onClick={save} disabled={saving} className="w-full font-semibold" data-testid="notif-save">Enregistrer les préférences</Button>
          </div>
        </TabsContent>

        <TabsContent value="supports">
          <div className="rounded-2xl border border-border bg-card overflow-hidden">
            {supports.length === 0 ? <p className="text-center text-muted-foreground py-12">Aucun soutien pour l'instant.</p> :
              supports.map((s) => (
                <div key={s.id} className="flex items-center justify-between px-4 py-3 border-b border-border last:border-0">
                  <div><p className="text-sm font-semibold">{s.media_name}</p><p className="text-[11px] text-muted-foreground font-mono">{s.reference} · {new Date(s.created_at).toLocaleDateString("fr-FR")}</p></div>
                  <div className="text-right"><p className="font-heading font-bold">${s.amount}</p><span className={`text-[10px] font-medium ${s.status === "completed" ? "text-emerald-600" : "text-amber-600"}`}>{s.status === "completed" ? "Confirmé" : "En attente"}</span></div>
                </div>
              ))}
          </div>
        </TabsContent>
      </Tabs>

      <button onClick={logout} className="flex items-center gap-2 mx-auto mt-8 text-sm text-rdcred font-medium hover:underline" data-testid="profile-logout"><LogOut size={16} /> Se déconnecter</button>
    </div>
  );
}
